import { ApolloQueryResult, useReactiveVar } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Modal, Button, Col, Spinner } from "react-bootstrap";
import { editPremium } from "../../../graphql/state";
import {
  UsersWithPaginationQuery,
  useUserChangePremiumMutation,
  useUserDeletePremiumMutation,
} from "../../../graphql/userAdministration.hooks";

type Props = {
  showModal: boolean;
  handleCloseModal: () => void;
  refetchUsers: () => Promise<ApolloQueryResult<UsersWithPaginationQuery>>;
};

function formatDateForInput(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  return `${yyyy}-${mm}-${dd}`;
}

const formatBackendDateForInput = (stringDate: string) => {
  const d = new Date(stringDate);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${year}-${month}-${day}`;
};

function isExpired(stringDate: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = new Date(stringDate);
  inputDate.setHours(0, 0, 0, 0);

  return inputDate < today;
}

export const EditPremiumModal = ({
  showModal,
  handleCloseModal,
  refetchUsers,
}: Props) => {
  const editPremiumModal = useReactiveVar(editPremium);

  const [type, setType] = useState<string>("month");
  const [date, setDate] = useState<string>("");

  const [changePremium, { loading: changeLoading }] =
    useUserChangePremiumMutation();
  const [deletePremium, { loading: deleteLoading }] =
    useUserDeletePremiumMutation();

  const closeModal = () => {
    setDate("");
    setType("month");
    handleCloseModal();
  };

  useEffect(() => {
    const date = editPremiumModal?.user?.userPremium?.userPremiumEndDate;

    setType(editPremiumModal?.user?.userPremium?.userPremiumType || "month");
    if (!isExpired(date)) {
      setDate(date ? formatBackendDateForInput(date) : "");
    }
  }, [editPremiumModal?.user?.userPremium]);

  const tryChangeUserPlan = async () => {
    try {
      const { data } = await changePremium({
        variables: { userId: editPremiumModal?.user?.id, type, endDate: date },
      });
      if (data?.userChangePremium) {
        refetchUsers();
        closeModal();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const tryDeleteUserPremium = async () => {
    try {
      const { data } = await deletePremium({
        variables: { userId: editPremiumModal?.user?.id },
      });

      if (data?.userDeletePremium) {
        refetchUsers();
        closeModal();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title>User premium edit</Modal.Title>
      </Modal.Header>
      <div className="p-md-3">
        <p className="fs-6 text-center mb-3 mb-md-5 px-3 px-md-0 text-gray">
          Editing premium for user with username:{" "}
          {editPremiumModal?.user?.email}
        </p>
        <div className="premiumSettingsBlock px-3 px-md-0">
          <div className="mb-3">
            <label htmlFor="dateInput" className="form-label">
              Premium due date
            </label>
            <input
              type="date"
              className="form-control"
              id="dateInput"
              name="date"
              value={date}
              onChange={(e) =>
                setDate(formatDateForInput(new Date(e.target.value)))
              }
            />
          </div>
          <div className="mb-3">
            <label htmlFor="dateInput" className="form-label">
              Premium type
            </label>
            <select
              className="col-12 border rounded"
              name="type"
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={{ padding: "15px 7px" }}
            >
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </div>
        </div>
        <div className="modalMobileBtns d-flex align-items-center justify-content-between gx-md-2">
          <Col className="modalMobileBtn" md={5}>
            <Button
              variant="outline-dark"
              onClick={tryChangeUserPlan}
              className="w-100 fw-normal dark-hover"
              disabled={
                changeLoading ||
                !date ||
                date.includes("NaN") ||
                isExpired(date)
              }
            >
              Change
              {changeLoading && (
                <Spinner
                  animation="border"
                  role="status"
                  className="ms-2"
                ></Spinner>
              )}
            </Button>
          </Col>
          <Col className="modalMobileBtn" md={5}>
            <Button
              variant="danger"
              className="w-100 text-white"
              onClick={tryDeleteUserPremium}
              disabled={deleteLoading || !editPremiumModal?.user?.activePremium}
            >
              Delete
              {deleteLoading && (
                <Spinner
                  animation="border"
                  role="status"
                  className="ms-2"
                ></Spinner>
              )}
            </Button>
          </Col>
        </div>
      </div>
    </Modal>
  );
};
